import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Stack,
  Grid,
  Typography,
  Chip,
  Button,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { usePlatformOrganisation } from '../../hooks/usePlatformOrganisation';
import { useResendOwnerInvitation } from '../../hooks/useResendOwnerInvitation';
import { useSnackbarStore } from '../../../../shared/stores/snackbarStore';
import { ResendOwnerInvitationDialog } from '../molecules/ResendOwnerInvitationDialog';
import type { OwnerInvitationResponse } from '@billda/api';

interface OrgOverviewTabProps {
  orgId: string;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getInvitationChipProps(status: string) {
  switch (status) {
    case 'Active':
      return { label: 'Active', color: 'success' as const };
    case 'InvitationSent':
      return { label: 'Invited', color: 'warning' as const };
    case 'Expired':
      return { label: 'Expired', color: 'error' as const };
    default:
      return { label: status, color: 'default' as const };
  }
}

function getResendCountLabel(resendCount: number): string {
  if (resendCount === 0) {
    return 'Not yet resent';
  }
  if (resendCount === 1) {
    return 'Resent once';
  }
  return `Resent ${resendCount} times`;
}

function FieldRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500} sx={{ textAlign: 'right' }}>
        {children}
      </Typography>
    </Box>
  );
}

function InvitationMeta({
  invitation,
}: {
  invitation: OwnerInvitationResponse;
}) {
  const now = new Date();
  const expiresAt = new Date(invitation.expiresAt);
  const isExpired = expiresAt.getTime() <= now.getTime();
  const expiryLabel = isExpired
    ? `Expired ${formatDateTime(invitation.expiresAt)}`
    : `Expires ${formatDateTime(invitation.expiresAt)}`;

  return (
    <Typography variant="body2" color="text.secondary">
      {`Sent ${formatDateTime(invitation.sentAt)} · by ${invitation.sentByName} · ${expiryLabel} · ${getResendCountLabel(invitation.resendCount)}`}
    </Typography>
  );
}

export const OrgOverviewTab = ({ orgId }: OrgOverviewTabProps) => {
  const { data, isLoading, error } = usePlatformOrganisation(orgId);
  const showSnackbar = useSnackbarStore(state => state.showSnackbar);
  const { mutate: resendInvite, isPending: isResending } =
    useResendOwnerInvitation();
  const [resendDialogOpen, setResendDialogOpen] = useState(false);

  const handleResendConfirm = () => {
    resendInvite(orgId, {
      onSuccess: () => {
        setResendDialogOpen(false);
        showSnackbar('Invitation resent', 'success');
      },
    });
  };

  if (isLoading && !data) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
          p: 3,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error?.message ?? 'Organisation not found'}
      </Alert>
    );
  }

  const { owner, invitation } = data;
  const invitationChip = invitation
    ? getInvitationChipProps(invitation.status)
    : { label: '—', color: 'default' as const };

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined" data-testid="org-overview-organisation-card">
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                Organisation
              </Typography>
              <Stack spacing={1.5}>
                <FieldRow label="Name">{data.name}</FieldRow>
                <FieldRow label="Slug">{data.slug}</FieldRow>
                <FieldRow label="Created">
                  {formatDate(data.createdAt)}
                </FieldRow>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined" data-testid="org-overview-owner-card">
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                Owner
              </Typography>
              <Stack spacing={1.5}>
                <FieldRow label="Name">{owner?.name ?? '—'}</FieldRow>
                <FieldRow label="Email">{owner?.email ?? '—'}</FieldRow>
                <FieldRow label="Membership">
                  {owner?.membershipStatus ?? '—'}
                </FieldRow>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box
        sx={{
          mt: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 2,
        }}
        data-testid="owner-invitation-block"
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: invitation ? 1 : 0,
          }}
        >
          <Typography variant="subtitle2">Owner invitation</Typography>
          <Chip
            label={invitationChip.label}
            color={invitationChip.color}
            size="small"
            sx={{ fontSize: '0.7rem', height: 22 }}
          />
        </Box>

        {invitation && <InvitationMeta invitation={invitation} />}

        {invitation?.canResend && (
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={() => setResendDialogOpen(true)}
            disabled={isResending}
            data-testid="overview-resend-button"
            sx={{ mt: 2 }}
          >
            Resend invitation
          </Button>
        )}
      </Box>

      <ResendOwnerInvitationDialog
        open={resendDialogOpen}
        email={owner?.email ?? ''}
        organisationName={data.name}
        isLoading={isResending}
        onConfirm={handleResendConfirm}
        onCancel={() => setResendDialogOpen(false)}
      />
    </Box>
  );
};
